// React
import React, { useState, useMemo, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
// Material UI
import {
    Box,
    Typography,
    Stepper,
    Step,
    StepLabel,
    AppBar,
    Toolbar,
    makeStyles,
    CircularProgress,
    Divider,
} from "@material-ui/core";
import Button from "components/CustomButtons/Button.js";
import NotificationContext from "views/Components/Context.js";
import HeadingWithDescription from "components/Heading/HeadingWithDescription";
// Styles
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import { stepperStyles, QontoConnector } from "views/Styles/stepperStyles";
import appbarStyles from "views/Styles/appbarStyles";

import ReviewScreen from "../ReviewScreen";
// NEW: import your step titles/descriptions
import stepLabels from "./stepLabels";
import ProjectDetailsScreen from "./ProjectDetailsScreen";

// Model templates (names + fetch by name)
import { modelTemplateNames, fetchTemplateMd } from "../Model Development/modelTemplates";
import {
    complianceTemplates,
    fetchComplianceMdByName,
} from "../Model Development/complianceTemplates";

import {
    getModelDevelopmentProjectDetails,
    updateModelDevelopmentProjectDetails,
    setNextStep,
    saveNewVersion,
    generateOverview,
    generateModelDevelopmentPlan,
    publishProject
} from "views/APIs/ToolboxUtility";
import { fetchAllProjectsAPI } from "views/APIs/ProjectsUtility";
import { Assignment, Description } from "@material-ui/icons";
import NextStepsSection from "./NextStepsSection";
import { getUser } from "views/APIs/APIUtility";
import { createJSONProjectAPI } from "views/APIs/APIUtility";
import { updateJSONProjectAPI } from "views/APIs/APIUtility";
import { createModelValidationProjectAPI } from "views/APIs/ToolboxUtility";

const useStyles = makeStyles((theme) => ({
    ...styles,
    ...stepperStyles(theme),
    ...appbarStyles(theme),
}));
export function getVersionOptions(doc, markdownName) {
    const section = doc[markdownName];
    if (!Array.isArray(section)) return [];

    return section.map(e => e.version);
}

function getMarkdownEntry(doc, markdownName, version) {
    if (version === 0) version = 1;
    const section = doc?.[markdownName];
    if (!Array.isArray(section)) return "";
    const entry = section[version - 1];
    return entry ? entry['markdown'] : "";
}

function getMarkdownName(activeStep) {
    const map = {
        1: "model_development_plan_md",
        2: "model_description_md",
        3: "system_use_case_md",
        4: "regulatory_compliance_md",
        5: "overview_md",
        6: "final_plan_md",
    };
    return map[Number(activeStep)] || "";
}
function getFileList(doc) {
    if (!doc || !Array.isArray(doc.uploaded_files)) return [];

    return doc.uploaded_files.map(file => ({
        id: file.file_id,
        name: file.name
    }));
}
const LoadingIcon = () => <CircularProgress size={22} />;
export default function ModelBoard(props) {
    const { projectId } = props.match.params;
    const [markdownContent, setMarkdownContent] = useState("");
    const classes = useStyles();
    const [activeStep, setActiveStep] = useState(0);
    const [projectName, setProjectName] = useState("Loading...");
    const [projectDescription, setProjectDescription] = useState("");
    const [projectList, setProjectList] = useState([])
    const [associatedProjectId, setAssociatedProjectId] = useState("")
    const [modelProject, setModelProject] = useState({})
    const [versionOptions, setVersionOptions] = useState([])
    const [version, setVersion] = useState(0);
    const [fileList, setFileList] = useState([])
    const [loading, setLoading] = useState({
        show: false,
        type: "",
        message: ""

    });
    const history = useHistory();

    const [maxStep, setMaxStep] = useState(0);
    const [originalMarkdownContent, setOriginalMarkdownContent] = useState("");
    const { showNotification } = useContext(NotificationContext);

    const handleGenerateValidationPlan = async () => {

        setLoading({
            show: true,
            type: "info",
            message: "Generating validation plan..."
        });

        let formData = new FormData();
        formData.append("title", projectName + " → Validation Plan");
        formData.append("description", "This is a validation plan generated from a model development project.");
        const response = await createModelValidationProjectAPI(formData);
        showNotification("Validation plan created successfully", "success");
        setLoading({
            show: true,
            type: "success",
            message: "Redirecting to validation plan..."
        });

        window.location.href = `/create/model_validation/model/${response._id}`;
    }

    const handleConvertToTemplate = async () => {

        setLoading({
            show: true,
            type: "info",
            message: "Redirecting you to the template..."
        });

        let formData = new FormData();

        formData.append("name", projectName.trim() + " → Template");
        formData.append("description", "This is a template created from a model development project.");
        let response = await createJSONProjectAPI(formData);
        showNotification("Template created successfully", "success");
        setLoading({
            show: true,
            type: "success",
            message: "Linking template to project..."
        });

        formData = new FormData();
        formData.append("json_project_id", response._id);
        formData.append("name", projectName.trim() + " → Template");
        formData.append("description", "This is a template created from a model development project.");
        formData.append("associated_project_id", associatedProjectId);
        formData.append("linked_project_id", projectId);
        await updateJSONProjectAPI(formData);
        showNotification("Template linked successfully", "success");

        setLoading({
            show: true,
            type: "success",
            message: "Template linked successfully"
        });

        window.location.href = `/create/form-design/${response._id}`;
    }

    const nextSteps = [
        // {
        //     key: "validation",
        //     title: "Generate Model Validation Plan",
        //     description: "Create a validation plan for the current model development plan.",
        //     icon: <Assignment />,
        //     actionLabel: "Generate",
        //     onClick: handleGenerateValidationPlan,
        // },
        {
            key: "convert-template",
            title: "Convert to interactive form",
            description: "Turn this model development plan into an interactive form.",
            icon: <Description />,
            actionLabel: "Convert",
            onClick: handleConvertToTemplate,
        },
    ];

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const resp = await getModelDevelopmentProjectDetails(projectId);
                if (!alive) return;
                setModelProject(resp);
                setProjectName(resp.title || "Unnamed Project");
                setProjectDescription(resp.description || "");
                setAssociatedProjectId(resp.associated_project_id || "");
                setActiveStep(resp.step || 0);
                setFileList(getFileList(resp));
            } catch (e) {
                console.error(e);
            }
        })();
        return () => { alive = false; }; // proper cleanup function
    }, [projectId]);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const resp = await fetchAllProjectsAPI();
                if (!alive) return;
                setProjectList(resp || []);
            } catch (e) {
                console.error(e);
            }
        })();
        return () => { alive = false; };
    }, [projectId]);

    useEffect(() => {
        if (activeStep > 0) {
            setVersionOptions(modelProject[getMarkdownName(activeStep)] || [])
        }
    }, [activeStep]);

    useEffect(() => {
        setVersion(versionOptions.length > 0 ? versionOptions[versionOptions.length - 1].version : 0)
    }, [versionOptions]);

    useEffect(() => {
        if (version !== null) {
            setMarkdownContent(getMarkdownEntry(modelProject, getMarkdownName(activeStep), version) || "");
            setOriginalMarkdownContent(getMarkdownEntry(modelProject, getMarkdownName(activeStep), version) || "");
        }
    }, [version, activeStep]);

    useEffect(() => {
        setMaxStep(modelProject?.step || 0);
    }, [modelProject]);

    const handleStepClick = (index) => {
        if (index > maxStep) return;
        setActiveStep(index);
    };


    const onDropDownSelection = async (value) => {
        let md = "";
        if (value.length === 0) {
            md = ""
        }
        else if (activeStep === 2) {
            md = await fetchTemplateMd(value[value.length - 1]);
        } else if (activeStep === 4) {
            md = await fetchComplianceMdByName(value[value.length - 1]);
        }

        setMarkdownContent(md);
    };

    const dropdownOptions = useMemo(() => {
        if (activeStep === 2) {
            return modelTemplateNames;
        }
        if (activeStep === 4) return complianceTemplates
        return [];
    }, [activeStep]);

    const meta = stepLabels[activeStep]
    const isMultiSelect = false;

    const nextLabel = activeStep === 6 ? "Publish" : "Next"

    const onSave = async () => {
        try {
            const mdType = getMarkdownName(activeStep);
            if (mdType) {
                const res = await saveNewVersion(projectId, mdType, markdownContent);
                setModelProject(res);
                setVersionOptions((prev) => [...prev, { version: version + 1, markdown: markdownContent, timestamp: new Date().toISOString() }])
                setVersion(version + 1)
                showNotification("Version saved successfully!", "success");
            }
        } catch (e) {
            showNotification(`Error saving version: ${e.message}`, "error");
        }
    }


    const onNext = async () => {
        try {
            setLoading({
                show: true,
                type: "next",
                message: "Processing your files. This might take a few minutes..."
            });
            if (activeStep === 4) {
                setLoading({
                    show: true,
                    type: "next",
                    message: "Generating Overview. This might take a few minutes..."
                });
                const res = await generateOverview(projectId);
                setModelProject(res);
            }
            else if (activeStep === 5) {
                setLoading({
                    show: true,
                    type: "next",
                    message: "Generating Model Development Plan. This might take a few minutes..."
                });
                const res = await generateModelDevelopmentPlan(projectId);
                setModelProject(res);
            }
            if (activeStep < 6) {
                if (activeStep === 0)
                    await onUpdate();
                else if (markdownContent !== getMarkdownEntry(modelProject, getMarkdownName(activeStep), version)?.markdown)
                    await onSave();
                if (maxStep < 6) {
                    if (activeStep === maxStep) {
                        setMaxStep(activeStep + 1);
                    }
                }
                setActiveStep(activeStep + 1);
                const data = await setNextStep(projectId, activeStep + 1);
                setMaxStep(activeStep + 1);
            }
            if (activeStep === 6) {
                const res = await publishProject(projectId);
                showNotification("Project published successfully!", "success");
            }
            setLoading({
                show: false,
                type: "",
                message: ""
            });
        } catch (e) {
            setLoading({
                show: false,
                type: "",
                message: ""
            });
        }
    }

    const onUpdate = async () => {
        modelProject.title = projectName;
        modelProject.description = projectDescription;
        modelProject.associated_project_id = associatedProjectId;
        const res = await updateModelDevelopmentProjectDetails(projectId, modelProject);
        setModelProject(res);
    }


    return (
        <Box>
            <Divider style={{ marginBottom: 16 }} />
            <HeadingWithDescription
                title={projectName || "Unnamed Project"}
                shortDescription={projectDescription}
                identifier="model"
                showEditIcon={false}
                dialogTitle="Edit Project Details"
                onChange={async (updatedData) => {
                    setProjectName(updatedData.title);
                    setProjectDescription(updatedData.shortDescription);
                    modelProject.title = updatedData.title;
                    modelProject.description = updatedData.shortDescription;
                    await updateModelDevelopmentProjectDetails(projectId, modelProject);
                    showNotification("Project details updated successfully", "success");
                }}
            />

            <AppBar 
                position="static" 
                className={classes.header}
                style={{
                    border: "2px solid #20677b",
                    borderRadius: "6px",
                    margin: 0,
                    marginTop: -16,
                    padding: 0,
                    minHeight: "40px",
                }}
            >
                <Toolbar style={{ minHeight: "40px", padding: "4px 8px" }}>
                    <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                        {/* Stepper: numbered circles + step labels */}
                        <Stepper
                            activeStep={activeStep}
                            connector={<QontoConnector />}
                            size="small"
                            className={classes.stepperRoot}
                            style={{ 
                                flexGrow: 1,
                                margin: 0,
                                padding: "4px 0"
                            }}
                        >
                            {stepLabels.map((s, index) => {
                                const isDisabled = index > maxStep;
                                return (
                                    <Step 
                                        key={`${s.step_title}-${index}`} 
                                        onClick={() => handleStepClick(index)}
                                        style={{ padding: "0 4px" }}
                                    >
                                        <StepLabel
                                            StepIconComponent={loading.show && index === activeStep ? LoadingIcon : undefined}
                                            StepIconProps={!isDisabled ? {
                                                classes: {
                                                    root: classes.stepIcon,
                                                    active: classes.activeStepIcon,
                                                    completed: classes.completedStepIcon,
                                                },
                                            } : {}}
                                            sx={{
                                                "& .MuiStepLabel-label": {
                                                    display: "none",
                                                },
                                                "&:hover .MuiStepLabel-label": {
                                                    display: "block",
                                                    position: "absolute",
                                                    backgroundColor: "#20677b",
                                                    color: "white",
                                                    padding: "4px 8px",
                                                    borderRadius: "4px",
                                                    fontSize: "0.7rem",
                                                    whiteSpace: "nowrap",
                                                    marginTop: "8px",
                                                    zIndex: 1000,
                                                    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                                                },
                                                "& .MuiStepLabel-iconContainer": {
                                                    paddingRight: 0,
                                                }
                                            }}
                                        >
                                            {s.step_title}
                                        </StepLabel>
                                    </Step>
                                )
                            })}
                        </Stepper>
                    </Box>
                </Toolbar>
            </AppBar>

            {loading.show ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <CircularProgress className={classes.loaderClass} />
                    <Typography variant="h6" style={{ marginLeft: 16, textTransform: "none" }}>
                        {loading.message}
                    </Typography>
                </Box>
            ) : activeStep === 0 ? (
                <ProjectDetailsScreen
                    name={projectName}
                    setName={setProjectName}
                    onNext={onNext}
                    description={projectDescription}
                    setDescription={setProjectDescription}
                    projectsList={projectList}
                    associatedProjectId={associatedProjectId}
                    setAssociatedProjectId={setAssociatedProjectId}
                    onUpdate={onUpdate}
                    projectId={projectId}
                    fileList={fileList}
                    setFileList={setFileList}
                />
            ) : (
                <Box pt={2}>
                    <ReviewScreen
                        title={meta.step_title}
                        description={meta.step_description}
                        dropdown_label={meta.dropdown_label}
                        dropdown_options={dropdownOptions}
                        isMultiSelect={isMultiSelect}
                        markdownContent={markdownContent}
                        setMarkdownContent={setMarkdownContent}
                        onDropDownSelection={onDropDownSelection}
                        versionOptions={versionOptions}
                        version={version}
                        setVersion={setVersion}
                        onNext={onNext}
                        onSave={onSave}
                        nextLabel={nextLabel}
                        setLoading={setLoading}
                        originalMarkdownContent={originalMarkdownContent}
                    />
                    {activeStep === 6 && (
                        <NextStepsSection steps={nextSteps} loadingKey={false} />
                    )}
                </Box>
            )}
        </Box>
    )
}
